<template>
<section class="page-section">
    <div id="mix-container" tabindex="-1"
        @keypress.space.prevent="$refs.waveform.playPause"
        @keydown.ctrl.prevent="$refs.waveform.playIntro"
        @keydown.alt.prevent="$refs.waveform.playOutro"
        @keypress.u="$refs.waveform.setFadeIn"
        @keypress.i="$refs.waveform.setIntro"
        @keypress.m="$refs.waveform.setMixPoint"
        @keypress.o="$refs.waveform.setOutro"
        @keypress.p="$refs.waveform.setFadeOut"
        @keypress.c="$refs.waveform.setCue"
        @keydown.enter.exact="$refs.waveform.playCue"
        @keydown.down.exact="$refs.waveform.seek(-100)"
        @keydown.up.exact="$refs.waveform.seek(100)"
        @keydown.left.exact="$refs.waveform.seek(-10)"
        @keydown.right.exact="$refs.waveform.seek(10)"
        @keydown.delete.exact="$refs.waveform.stop"
        @keyup.16="$refs.waveform.setRaw(false)"
        @keydown.16="$refs.waveform.setRaw(true)"
    >
        <div v-if="!waveIsReady" class="loading">
            <div class="progress-bar" :style="progressBarStyle"></div>
            <div class="progress-data">
                <div class="size">
                    <span class="label">Transferred: </span>
                    <span class="transferred">{{ formatSize(progress.transferred, 3) }}</span>
                    <span class="total"> / {{ formatSize(progress.total, 3) }}</span>
                </div>
                <div class="pct">
                    <span class="label">Complete: </span>
                    <span>{{ progress.percentage }} %</span>
                </div>
            </div>
        </div>
        <div class="waveform-tools">
            <div>
                <waveform-map ref="map" :blob="blob" @zoom="onMapZoom" @move="onMapMove"></waveform-map>
                <wave-form ref="waveform"
                           :fadeInStart="this.track.mixIntroPosition - this.track.mixFadeInDuration"
                           :fadeInStop="this.track.mixIntroPosition"
                           :fadeOutStart="this.track.mixExtroPosition"
                           :fadeOutStop="this.track.mixExtroPosition + this.track.mixFadeOutDuration"
                           :mixPoint="(this.track.mixExtroPosition + this.track.mixFadeOutDuration) - this.track.mixDuration"
                           :blob="blob"
                           @positions-changed="onPositionsChanged"
                           @scroll="onWaveFormScroll"
                           @ready="onWaveFormReady"
                           @audioprocess="onAudioProcess"
                           @paused="onPaused"></wave-form>
                <demo-tracks ref="demo"
                             :introBlob="introBlob"
                             :intro="{mixPoint:5000}"
                             :outroBlob="extroBlob"
                             :fadeInStart="this.track.mixIntroPosition - this.track.mixFadeInDuration"
                             :fadeInStop="this.track.mixIntroPosition"
                             :fadeOutStart="this.track.mixExtroPosition"
                             :fadeOutStop="this.track.mixExtroPosition + this.track.mixFadeOutDuration"
                             :mixPoint="(this.track.mixExtroPosition + this.track.mixFadeOutDuration) - this.track.mixDuration"></demo-tracks>
            </div>
        </div>

        <div class="form">
            <div class="smallfield fade-in">
                <text-input label="Fade in" :originalValue="track.mixFadeInDuration"  :maxlength="track.mixIntroPosition" type="number" @input="onFadeInput('in', $event)"><div slot="append">ms</div></text-input>
            </div>
            <div class="smallfield intro">
                <audio-time-input label="Intro" v-model="track.mixIntroPosition"/>
            </div>
            <div class="smallfield flex mixpoint">
                <audio-time-input label="Mix point" :value="getMixPosition(track)" @input="updateMixDuration(track, $event)"/>
            </div>
            <div class="smallfield flex extro">
                <audio-time-input label="Outro" v-model="track.mixExtroPosition"/>
            </div>
            <div class="smallfield flex fade-out">
                <text-input label="Fade out" :originalValue="track.mixFadeOutDuration" type="number" :maxlength="track.duration - track.mixExtroPosition" @input="onFadeInput('out', $event)"><div slot="append">ms</div></text-input>
            </div>
        </div>
        <div class='control-container'>
            <div class="controls">
                <a href="#" @click.prevent="$refs.waveform.playPause"><div class="key">Shift</div><div class="label">Mix/raw</div></a>
                <a href="#" @click.prevent="$refs.waveform.playPause"><div class="key space">Space</div><div class="label">Play/pause</div></a>
                <a href="#" @click.prevent="$refs.waveform.playIntro"><div class="key">Ctrl</div><div class="label">Play intro</div></a>
                <a href="#" @click.prevent="$refs.waveform.playOutro"><div class="key">Alt</div><div class="label">Play outro</div></a>
                <div class="spacer"></div>
                <a href="#" @click.prevent="$refs.waveform.playCue"><div class="key enter"><i class="fa fa-level-down-alt"></i></div><div class="label">Play cue</div></a>
                <a href="#" @click.prevent="$refs.waveform.stop"><div class="key stop"><i class="fa fa-backspace"></i></div><div class="label">Stop</div></a>
            </div>
            <div class="controls">
                <a href="#" @click.prevent="$refs.waveform.setFadeIn"><div class="key u">U</div><div class="label">Set FadeIn</div></a>
                <a href="#" @click.prevent="$refs.waveform.setIntro"><div class="key i">I</div><div class="label">Set Intro</div></a>
                <a href="#" @click.prevent="$refs.waveform.setMixPoint"><div class="key m">M</div><div class="label">Set Mix Point</div></a>
                <a href="#" @click.prevent="$refs.waveform.setOutro"><div class="key o">O</div><div class="label">Set Outro</div></a>
                <a href="#" @click.prevent="$refs.waveform.setFadeOut"><div class="key p">P</div><div class="label">Set FadeOut</div></a>
                <div class="spacer"></div>
                <a href="#" @click.prevent="$refs.waveform.setCue"><div class="key c">C</div><div class="label">Set cue</div></a>
            </div>
            <div class="controls">
                <a href="#"><div class="key left" @click.prevent="$refs.waveform.seek(-10)"><i class="fa fa-chevron-left"></i></div><div class="key" @click.prevent="$refs.waveform.seek(10)"><i class="fa fa-chevron-right"></i></div><div class="label">10ms</div></a>
                <a href="#"><div class="key left" @click.prevent="$refs.waveform.seek(-100)"><i class="fa fa-chevron-up"></i></div><div class="key" @click.prevent="$refs.waveform.seek(100)"><i class="fa fa-chevron-down"></i></div><div class="label">100ms</div></a>
                <div class="spacer"></div>
            </div>
        </div>
        <footer class="">
            <Button @click.stop="$router.go(-1)" :empty="true">Cancel</Button>
            <button class="default" @click.prevent="prevTrack" v-if="trackList.length > 1" :disabled="listIndex === 0">Previous</button>
            <div class="spacer ml-auto"></div>
            <button class="default ml-2" @click.prevent="nextTrack" v-if="trackList.length > 1" :disabled="listIndex === trackList.length - 1">Next</button>
            <button class="empty ml-2" @click.prevent="saveAndStay">Save</button>
            <button class="primary ml-2" @click.prevent="saveAndNext" v-if="trackList.length && !isLastTrack">Save &amp; next</button>
            <button class="primary ml-2" @click.prevent="saveAndClose" v-if="!trackList.length || isLastTrack">Save &amp; close</button>
        </footer>
        <!--<demo-track-selector ref="demoTrackSelector" :stationUid="stationUid"></demo-track-selector>-->
    </div>
</section>
</template>

<script>
    import Button from '@/components/common/Button';
    import { mapState, mapActions } from 'vuex';
    import { HTTP } from '@/utils/http';
    import Apis from '@/apis/index';
    import Constants from '@/store/constants';
    import Translations from '@/store/translations';
    import { formatDate, formatDuration, formatSize, formatArtists, formatStyles, formatTags, formatLanguages } from '@/lib/formatting';
    import moment from 'moment';


    //	import Modal from'@/components/common/Modal.vue';
    //-----------------------------------------------------------------------------------
    import ListField from '@/components/common/ListField.vue';
    import TextInput from '@/components/common/TextInput.vue';
    import AudioTimeInput from '@/components/common/AudioTimeInput.vue';
    import DemoTrackSelector from './DemoTrackSelector.vue';
    import DemoTracks from './DemoTracks.vue';
    //-----------------------------------------------------------------------------------
    import WaveForm from './WaveForm.vue';
    import WaveformTimeline from './WaveformTimeline.vue';
    import WaveformMap from './WaveformMap.vue';
    //-----------------------------------------------------------------------------------
    import {getRessources} from "@/utils/getRessources";

    export default {
        name: "TrackCuePoints",
        props: {
            tracks: { type: Array, default: () => [] },
            index: { type: Number, default: 0 },
            stationUid: String,
        },
        components: {
            ListField, TextInput, AudioTimeInput, DemoTrackSelector,
            WaveForm, WaveformTimeline, WaveformMap, DemoTracks, Button
        },
        computed: {
            // ...mapState('tracks', ['currentTrack']),
            ...mapState({
                // tracks: state => state.tracks,
                currentTrack: state => state.tracks.currentTrack
            }),
            Constants: () => Constants,
            formatDuration: () => formatDuration,
            formatSize: () => formatSize,
            formatDate: () => formatDate,
            formatArtists: () => formatArtists,
            formatStyles: () => formatStyles,
            formatTags: () => formatTags,
            formatLanguages: () => formatLanguages,
            currentStationUid: () => this.$route.params.stationUid,
            Translations: () => Translations,
            isLastTrack: function () { return this.listIndex === this.trackList.length - 1 },
            progressBarStyle: function () {
                return {
                    width: this.progress.percentage + "%"
                }
            }
        },
        watch: {
            "$route.params": function () {
                // console.log("Load Track from route change");
                //console.log('route change');
                this.loadTrack(this.$route.params);
            },
            "currentTrack": function () {
                //console.log('change currentTrack');
                // console.log("Current Track updated");
                this.track = Object.assign({}, this.currentTrack);
            },
            'track': {
                handler: function (track) {
                    if(track.mixExtroPosition < track.mixIntroPosition){
                        this.track.mixExtroPosition = track.mixIntroPosition + 500
                        //console.log('lower than intro');
                    }
                },
                deep: true
            }
            // tracks: 'setTrack',
            // index: 'setTrack',
        },
        data() {
            return {
                tabIndex: 0,
                listIndex: 0,
                track: this.tracks[this.index] || {},
                blob: null,
                mapBlob: null,
                introBlob: null,
                extroBlob: null,
                waveIsReady: false,
                eta: 0,
                trackList: [],
                prevTrackId: '',
                nextTrackId: '',
                prevRoute: null,
                progress: {
                    total: 0,
                    transferred: 0,
                    percentage: 0
                }
            }
        },
        beforeDestroy() {
            //console.log('beforeDestroy');
            this.reset();
        },
        beforeRouteEnter(to, from, next) {
            next(vm => {
                vm.prevRoute = from
                //console.log('prevrouter', from)
            })
        },
        beforeRouteUpdate(to, from, next) {
            this.reset();
            // called when the route that renders this component has changed,
            // but this component is reused in the new route.
            // For example, for a route with dynamic params `/foo/:id`, when we
            // navigate between `/foo/1` and `/foo/2`, the same `Foo` component instance
            // will be reused, and this hook will be called when that happens.
            // has access to `this` component instance.
            this.checkRoute(to.params.trackUid)
            next()
        },
        mounted() {
            this.track = Object.assign({}, this.currentTrack);
            this.checkRoute(this.$route.params.trackUid)
            window.addEventListener('resize', this.onWindowResize.bind(this), false)
            //------------------------------------------------------------------------------------
            // LOAD INTRO/EXTRO TRACK
            //------------------------------------------------------------------------------------
            fetch(`${getRessources('sounds', 'extro.mp3')}`)
                .then(resp => { console.log(resp); return resp.blob()} )
                .then(blob => {
                    this.introBlob = blob
                    this.extroBlob = blob
                })
                .catch(err => console.log(err))
            this.loadTrack(this.$route.params.stationUid, this.$route.params.trackUid);
            this.loadFile();
        },
        methods: {
            ...mapActions('tracks', ['loadTrack']),
            checkRoute(trackId) {
                if (this.$route.query && this.$route.query.track_ids) {
                    this.trackList = JSON.parse(this.$route.query.track_ids)
                    this.listIndex = this.trackList.indexOf(trackId)
                    if (this.listIndex > 0) {
                        this.prevTrackId = this.trackList[this.listIndex - 1]
                    }
                    if (this.listIndex < this.trackList.length - 1) {
                        this.nextTrackId = this.trackList[this.listIndex + 1]
                    }
                }
            },
            onPaused() {
                this.$refs.demo.pause()
            },
            onAudioProcess(pos) {
                this.$refs.demo.updateVolume(pos)
            },
            onPositionsChanged(pos) {
                // Checks for track Limits
                if (pos.fadeOutStart > this.track.duration) pos.fadeOutStart = pos.fadeOutStop = this.track.duration;
                if (pos.fadeOutStop > this.track.duration) pos.fadeOutStop = this.track.duration;
                if (pos.fadeInStop < 0) pos.fadeInStart = pos.fadeInStop = 0;
                if (pos.fadeInStart < 0) pos.fadeInStart = 0;
                if (pos.mixPoint < pos.fadeInStart) pos.mixPoint = pos.fadeInStart
                if (pos.mixPoint > pos.fadeOutStop) pos.mixPoint = pos.fadeOutStop

                this.track.mixIntroPosition = pos.fadeInStop
                this.track.mixExtroPosition = pos.fadeOutStart
                // Minimal Checks
                if (pos.mixExtroPosition < pos.mixIntroPosition) {
                    pos.mixExtroPosition = pos.mixIntroPosition;
                }

                if (pos.fadeInStart > pos.fadeInStop) {
                    pos.fadeInStart = pos.fadeInStop;
                }
                if (pos.fadeOutStart > pos.fadeOutStop) {
                    pos.fadeInStop = pos.fadeOutStart;
                }

                this.track.mixFadeInDuration = pos.fadeInStop - pos.fadeInStart
                this.track.mixFadeOutDuration = pos.fadeOutStop - pos.fadeOutStart
                this.track.mixDuration = pos.fadeOutStop - pos.mixPoint
            },
            updateMixDuration(track, pos) {
                // console.log('upd', pos)
                track.mixDuration = (track.mixExtroPosition + track.mixFadeOutDuration) - pos
            },
            getMixPosition(track) {
                return track.mixExtroPosition + track.mixFadeOutDuration - track.mixDuration
            },
            reset() {
                this.$refs.waveform.reset()
                this.$refs.map.reset()
                this.$refs.demo.reset()
                this.waveIsReady = false
            },
            loadFile() {
                //------------------------------------------------------------------------------------
                // LOAD FILE
                //------------------------------------------------------------------------------------
                let etaSet = false
                //this.etaInterval = setInterval(() => {
                //    this.eta = 100
                //}, 500)
                let that = this;
                HTTP.get(
                    /* Endpoint */
                    Apis.library.tracks.play
                    /* Config   */
                    , {
                        params: this.$route.params
                        , responseType: 'blob'
                        , onDownloadProgress: function (progress) {
                            //console.log("Download Progress : ", progress);
                            try {
                                var percentCompleted = progress.total <= 0 ? 0 : Math.round((progress.loaded * 100) / progress.total);
                                //progress.pct = progress.total ? (progress.transferred / progress.total) * 100 : 1
                                that.progress = {
                                    total: progress.total,
                                    transferred: progress.loaded,
                                    percentage: percentCompleted,
                                    remaining: progress.total - progress.loaded,
                                }
                            } catch (e) {
                                console.log(e);
                            }
                        }
                    }
                ).then((response) => {
                    //console.log("File response : ", response);
                    this.blob = response.data;
                    //console.log(this.blob);
                })
            },
            setTrack() {
            },
            onFadeInput(type, value) {
                if (type === 'in') {
                    console.log('in: ', value);
                    this.track.mixFadeInDuration = Math.max(Math.min(this.track.mixIntroPosition, parseInt(value, 10)), 0);
                }

                if (type === 'out') {
                    this.track.mixFadeOutDuration = Math.max(Math.min(this.track.duration - this.track.mixExtroPosition, parseInt(value, 10)), 0);
                }
            },
            onMapZoom(zoom) {
                //console.log("onMapZoom")
                this.$refs.waveform.zoom(zoom)
                //this.$refs.timeline.resize(this.$refs.waveform.getWidth(), zoom)
                this.$refs.demo.resize(this.$refs.waveform.getWidth(), zoom)
            },
            onMapMove(scrollRatio) {
                //console.log("onMapMove")
                this.$refs.waveform.scrollBy(scrollRatio)
            },
            onWaveFormReady() {
                //console.log("onWaveFormReady")
                this.waveIsReady = true
                //this.$refs.timeline.setDuration(this.$refs.waveform.wavesurfer.getDuration())
                setTimeout(() => {
                    //this.$refs.timeline.resize(this.$refs.waveform.getWidth(), this.$refs.map.getZoom())
                    this.$refs.demo.resize(this.$refs.waveform.getWidth(), this.$refs.map.getZoom())
                    this.$refs.demo.setDuration(this.$refs.waveform.wavesurfer.getDuration())
                })
            },
            onWindowResize() {
                //console.log("onWindowResize")
                //console.log('win resize', this.$refs.map.getZoom(), this.$refs.waveform.getWidth())
                this.$refs.waveform.zoom(this.$refs.map.getZoom())
                //this.$refs.timeline.resize(this.$refs.waveform.getWidth(), this.$refs.map.getZoom())
                this.$refs.demo.resize(this.$refs.waveform.getWidth(), this.$refs.map.getZoom())
            },
            onWaveFormScroll(ratio, scrollLeft) {
                console.log("onWaveFormScroll")
                this.$refs.map.scrollBy(ratio)
                //this.$refs.timeline.scrollTo(scrollLeft)
                this.$refs.demo.scrollTo(scrollLeft)
            },
            onTimelineScroll(ratio, scrollLeft) {
                console.log("onTimelineScroll")
                this.$refs.map.scrollBy(ratio)
                this.$refs.waveform.scrollTo(scrollLeft)
                this.$refs.demo.scrollTo(scrollLeft)
            },
            nextTrack() {
                this.reset()
                this.$router.push({ name: 'TrackMix', params: { stationUid: this.$route.params.stationUid, trackUid: this.nextTrackId }, query: { track_ids: JSON.stringify(this.trackList) } })
            },
            prevTrack() {
                this.reset()
                this.$router.go(-1)
            },

            saveAndStay() {
                this.saveTrack()
                  .then(() => {})
                  .catch(() => {})
            },
            saveAndClose() {
                this.saveTrack()
                  .then(() => this.close())
                  .catch(() => {})
            },
            saveAndNext() {
                this.saveTrack()
                  .then(() => this.nextTrack())
                  .catch(() => {})
            },
            close() {
                this.reset()
                // // const tmp = {name: this.prevRoute.name, params: this.prevRoute.params}
                // // console.log('closing', -1 - this.listIndex, tmp)
                // // this.prevRoute && this.$router.replace(tmp)
                this.$router.go(-1 - this.listIndex)
            },
            saveTrack() {
                 const data = {
                     mixIntroPosition: Math.round(this.track.mixIntroPosition),
                     mixExtroPosition: Math.round(this.track.mixExtroPosition),
                     mixFadeInDuration: Math.round(this.track.mixFadeInDuration),
                     mixFadeOutDuration: Math.round(this.track.mixFadeOutDuration),
                     mixDuration: Math.round(this.track.mixDuration)
                 }
                 return HTTP.post(Apis.library.tracks.update.mixPointers, {
                    stationUid: this.$route.params.stationUid,
                    trackUids: [this.track.uid],
                    value:data
                  })
                  .then(response => {
                    this.$store.dispatch('Toasts/addToast', {
                      title: 'Mix Points',
                      message: 'Mix Points saved with success.',
                      type: 'info'
                    });

                    return Promise.resolve(response)
                  })
                  .catch(e => {
                    this.$store.dispatch('Toasts/addToast', {
                      title: 'Error occurred',
                      message: e.data.errors[0].message,
                      type: 'danger'
                    });

                    return Promise.reject(e)
                  })

            }
        }
    }
</script>


<style lang="scss" scoped>
    #mix-container {
        position: relative;

        .loading {
            position: relative;
            height: auto;
        }

        .progress-data {
            letter-spacing:normal;
            display:flex;width:100%;justify-content:space-between;
            border:1px solid var(--COLOR_BRAND);
        }
    }
</style>
